import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  MapPin, Store, ArrowLeft, CheckCircle, Clock,
  Star, ChevronRight, Package, Navigation, DollarSign,
  AlertCircle, Sparkles, UserCheck,
} from "lucide-react";
import axios from "axios";
import AfficheMagasinsService from "../../../Services/Magasin/AfficheMagasinsService";
import DeplacementService from "../../../Services/Magasin/DeplacementService";
import { MagasinInfo } from "../../../Modeles/MagasinModel";
import HistoriqueCommande from "../../../Services/Admin/HistoriqueCommande";
import { NewSideBarMagasins } from "../../NewSidebarMagasin";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toNum = (v: any): number => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const getMagasinId = (m: MagasinInfo): number | string =>
  (m as any).IdMagasin ?? (m as any).id ?? (m as any).ID ?? (m as any)._id ?? "";

// ─── BUG FIX 1: Validate coordinates before using them ───────────────────────
// Returns true only if the point looks like a real geographic location.
// Filters out: null island (0,0), missing values, and out-of-range values.
const hasValidCoords = (lat: number, lng: number): boolean => {
  if (!lat || !lng) return false;
  if (lat === 0 && lng === 0) return false;       // null island — no coords set
  if (Math.abs(lat) > 90) return false;           // impossible latitude
  if (Math.abs(lng) > 180) return false;          // impossible longitude
  return true;
};

// ─── BUG FIX 2: Detect and correct swapped lat/lng ───────────────────────────
// For Morocco: real latitudes are ~27–36 (positive), real longitudes are ~-14 to -1.
// If lat is negative (or > 90) while lng looks like a latitude, the fields are swapped.
const normalizeCoords = (rawLat: number, rawLng: number): { lat: number; lng: number } => {
  // Definitive swap: lat is out of [-90,90] — swap unconditionally
  if (Math.abs(rawLat) > 90) return { lat: rawLng, lng: rawLat };

  // Heuristic for Morocco: if lat is negative but lng is a reasonable latitude (>0 & <90),
  // the fields were likely swapped in the API response.
  if (rawLat < 0 && rawLng > 0 && rawLng < 90) return { lat: rawLng, lng: rawLat };

  return { lat: rawLat, lng: rawLng };
};

// ─── BUG FIX 3: Guard haversineKm so 0,0 coords produce 0, not 3500+ km ─────
const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  if (!hasValidCoords(lat1, lon1) || !hasValidCoords(lat2, lon2)) return 0;
  const R = 6371;
  const toRad = (v: number) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ─── Geocode a free-text address via Nominatim ────────────────────────────────
const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
  if (!address) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1&countrycodes=ma`;
    const res = await fetch(url, { headers: { "Accept-Language": "fr" } });
    const json = await res.json();
    if (!json.length) return null;
    const lat = parseFloat(json[0].lat);
    const lng = parseFloat(json[0].lon);
    // Validate the geocoded result too
    if (!hasValidCoords(lat, lng)) return null;
    return { lat, lng };
  } catch {
    return null;
  }
};

// ─── Score: lower is better ───────────────────────────────────────────────────
// Primary = distance relay → client | Tiebreak = distance source → relay
const scoreRelay = (
  relayLat: number, relayLng: number,
  clientLat: number, clientLng: number,
  sourceLat: number, sourceLng: number,
): number => {
  const toClient = haversineKm(relayLat, relayLng, clientLat, clientLng);
  const fromSource = haversineKm(sourceLat, sourceLng, relayLat, relayLng);
  return toClient * 0.7 + fromSource * 0.3;
};

export interface SocieteLivraison {
  id?: number;
  NomSociete: string;
  AdresseSociete: string;
  Prix?: string;
  Status?: string;
}

// ─── Societe Card ─────────────────────────────────────────────────────────────
const SocieteCard = ({
  societe, selected, onClick,
}: {
  societe: SocieteLivraison & { _distanceFromSource: number };
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    style={{
      width: "100%", textAlign: "left",
      background: selected ? "linear-gradient(135deg,#eff6ff,#dbeafe)" : "#fff",
      border: `2px solid ${selected ? "#3b82f6" : "#e5e7eb"}`,
      borderRadius: 16, padding: "1.1rem 1.25rem",
      cursor: "pointer", position: "relative",
      transition: "border-color 0.2s, box-shadow 0.2s, transform 0.15s",
      boxShadow: selected ? "0 6px 24px rgba(59,130,246,0.15)" : "0 1px 6px rgba(0,0,0,0.04)",
    }}
    onMouseEnter={(e) => {
      if (!selected) {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.09)";
      }
    }}
    onMouseLeave={(e) => {
      if (!selected) {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 1px 6px rgba(0,0,0,0.04)";
      }
    }}
  >
    {selected && (
      <div style={{
        position: "absolute", top: "0.9rem", right: "0.9rem",
        width: 24, height: 24, borderRadius: "50%", background: "#3b82f6",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <CheckCircle size={14} color="white" strokeWidth={3} />
      </div>
    )}
    <div style={{ display: "flex", alignItems: "flex-start", gap: "0.875rem" }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12, flexShrink: 0,
        background: selected ? "#3b82f6" : "#f3f4f6",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Package size={20} color={selected ? "#fff" : "#9ca3af"} strokeWidth={2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: "0 0 4px", fontWeight: 800, fontSize: "0.9rem", color: "#111827" }}>
          {societe.NomSociete}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: "0.6rem" }}>
          <MapPin size={12} color="#9ca3af" strokeWidth={2} />
          <span style={{ fontSize: "0.78rem", color: "#6b7280" }}>{societe.AdresseSociete}</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
          {societe._distanceFromSource > 0 ? (
            <DistPill icon={<Navigation size={11} strokeWidth={2.5} />} label="de votre magasin " value={`${societe._distanceFromSource.toFixed(1)} km`} accent="#0369a1" />
          ) : societe._distanceFromSource === -1 ? (
            <DistPill icon={<Clock size={11} strokeWidth={2.5} />} label="Calcul..." value="" accent="#9ca3af" />
          ) : null}
          {societe.Prix ? (
            <DistPill icon={<DollarSign size={11} strokeWidth={2.5} />} label="Frais " value={societe.Prix === '-' ? "Non défini" : `${societe.Prix} MAD`} accent="#7e22ce" />
          ) : null}
        </div>
      </div>
    </div>
  </button>
);

// ─── Extended type ────────────────────────────────────────────────────────────
type MagasinWithDistance = MagasinInfo & {
  _lat: number;
  _lng: number;
  _distanceFromSource: number;
  _distanceToClient: number;
  _score: number;
  _isRecommended: boolean;
};

// ─── Recommendation badge ─────────────────────────────────────────────────────
const RecommendedBadge = () => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 9px", borderRadius: 20,
    background: "#0d9488", color: "#fff",
    fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.4px",
    textTransform: "uppercase",
  }}>
    <Sparkles size={9} strokeWidth={2.5} />
    Recommandé
  </span>
);

// ─── Distance pill ────────────────────────────────────────────────────────────
const DistPill = ({
  icon, label, value, accent,
}: { icon: React.ReactNode; label?: string; value: string; accent: string }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 10px",
    background: `${accent}14`,
    border: `1px solid ${accent}30`,
    color: accent,
    borderRadius: 8, fontSize: "0.74rem", fontWeight: 700,
    whiteSpace: "nowrap",
  }}>
    {icon}
    {label && <span style={{ opacity: 0.7, fontWeight: 600 }}>{label}</span>}
    {value}
  </span>
);

// ─── Magasin Card ─────────────────────────────────────────────────────────────
const MagasinCard = ({
  magasin, selected, onClick, commissionAmount, clientCoordsAvailable,
}: {
  magasin: MagasinWithDistance;
  selected: boolean;
  onClick: () => void;
  commissionAmount: number;
  clientCoordsAvailable: boolean;
}) => (
  <button
    onClick={onClick}
    style={{
      width: "100%", textAlign: "left",
      background: selected
        ? "linear-gradient(135deg,#fefce8,#fef9c3)"
        : magasin._isRecommended
        ? "linear-gradient(135deg,#f0fdfa,#ccfbf1)"
        : "#fff",
      border: `2px solid ${selected ? "#eab308" : magasin._isRecommended ? "#14b8a6" : "#e5e7eb"}`,
      borderRadius: 16, padding: "1.1rem 1.25rem",
      cursor: "pointer", position: "relative",
      transition: "border-color 0.2s, box-shadow 0.2s, transform 0.15s",
      boxShadow: selected
        ? "0 6px 24px rgba(234,179,8,0.15)"
        : magasin._isRecommended
        ? "0 4px 18px rgba(20,184,166,0.1)"
        : "0 1px 6px rgba(0,0,0,0.04)",
    }}
    onMouseEnter={(e) => {
      if (!selected) {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.09)";
      }
    }}
    onMouseLeave={(e) => {
      if (!selected) {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = magasin._isRecommended
          ? "0 4px 18px rgba(20,184,166,0.1)"
          : "0 1px 6px rgba(0,0,0,0.04)";
      }
    }}
  >
    {/* Selected tick */}
    {selected && (
      <div style={{
        position: "absolute", top: "0.9rem", right: "0.9rem",
        width: 24, height: 24, borderRadius: "50%",
        background: "#eab308",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <CheckCircle size={14} color="white" strokeWidth={3} />
      </div>
    )}

    <div style={{ display: "flex", alignItems: "flex-start", gap: "0.875rem" }}>
      {/* Icon */}
      <div style={{
        width: 44, height: 44, borderRadius: 12, flexShrink: 0,
        background: selected ? "#eab308" : magasin._isRecommended ? "#14b8a6" : "#f3f4f6",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Store size={20}
          color={selected || magasin._isRecommended ? "#fff" : "#9ca3af"}
          strokeWidth={2} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Name row */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3 }}>
          <p style={{ margin: 0, fontWeight: 800, fontSize: "0.9rem", color: "#111827" }}>
            {magasin.NomMagasin}
          </p>
          {magasin._isRecommended && <RecommendedBadge />}
        </div>

        {/* Address */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: "0.6rem" }}>
          <MapPin size={12} color="#9ca3af" strokeWidth={2} />
          <span style={{ fontSize: "0.78rem", color: "#6b7280" }}>{magasin.adresse_de_siege}</span>
        </div>

        {/* Pills */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
          {clientCoordsAvailable && magasin._distanceToClient > 0 && (
            <DistPill
              icon={<UserCheck size={11} strokeWidth={2.5} />}
              label="du client "
              value={`${magasin._distanceToClient.toFixed(1)} km`}
              accent={magasin._isRecommended ? "#0d9488" : "#16a34a"}
            />
          )}
          {magasin._distanceFromSource > 0 && (
            <DistPill
              icon={<Navigation size={11} strokeWidth={2.5} />}
              label="de vous "
              value={`${magasin._distanceFromSource.toFixed(1)} km`}
              accent="#0369a1"
            />
          )}
          <DistPill icon={<Star size={11} strokeWidth={2.5} />} value="5 étoiles" accent="#d97706" />
          <DistPill
            icon={<DollarSign size={11} strokeWidth={2.5} />}
            label="Commission "
            value={`${commissionAmount.toFixed(2)} MAD`}
            accent={selected ? "#92400e" : "#7e22ce"}
          />
        </div>

        {magasin._isRecommended && clientCoordsAvailable && (
          <p style={{
            margin: "0.5rem 0 0", fontSize: "0.76rem", color: "#0d9488", fontWeight: 600,
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <Sparkles size={11} strokeWidth={2.5} />
            Le plus proche de l'adresse du client
            {magasin._distanceToClient > 0 && ` · ${magasin._distanceToClient.toFixed(1)} km`}
          </p>
        )}
      </div>
    </div>
  </button>
);

// ─── Interactive SVG Map ───────────────────────────────────────────────────────
const InteractiveMap = ({
  magasins, societes, selectedMagasinId, selectedSocieteId, onSelectMagasin, onSelectSociete, refLat, refLng, clientLat, clientLng,
}: {
  magasins: MagasinWithDistance[];
  societes?: any[];
  selectedMagasinId: number | string | null;
  selectedSocieteId: number | null;
  onSelectMagasin: (id: number | string) => void;
  onSelectSociete: (id: number) => void;
  refLat: number; refLng: number;
  clientLat: number | null; clientLng: number | null;
}) => {
  const SVG_W = 680;
  const SVG_H = 360;
  const MARGIN = 72;

  const mappableMagasins = magasins.filter((m) => hasValidCoords(m._lat, m._lng)).map(m => ({...m, _type: 'magasin'}));
  const mappableSocietes = (societes || []).filter((s) => s.id && s._lat && s._lng && hasValidCoords(s._lat, s._lng)).map(s => ({...s, _type: 'societe'}));

  const items = [...mappableMagasins, ...mappableSocietes];

  const allLats = [
    ...items.map((m) => m._lat),
    refLat,
    ...(clientLat ? [clientLat] : []),
  ].filter(hasFinite);
  
  const allLngs = [
    ...items.map((m) => m._lng),
    refLng,
    ...(clientLng ? [clientLng] : []),
  ].filter(hasFinite);

  if (allLats.length === 0 || !hasValidCoords(refLat, refLng)) return null;

  function hasFinite(v: number) { return isFinite(v); }

  const minLat = Math.min(...allLats);
  const maxLat = Math.max(...allLats);
  const minLng = Math.min(...allLngs);
  const maxLng = Math.max(...allLngs);
  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;
  const latSpread = Math.max(maxLat - minLat, 0.02);
  const lngSpread = Math.max(maxLng - minLng, 0.02);
  const PAD = 0.3;
  const adjMinLat = centerLat - latSpread * (0.5 + PAD);
  const adjMaxLat = centerLat + latSpread * (0.5 + PAD);
  const adjMinLng = centerLng - lngSpread * (0.5 + PAD);
  const adjMaxLng = centerLng + lngSpread * (0.5 + PAD);
  const adjLatSpread = adjMaxLat - adjMinLat;
  const adjLngSpread = adjMaxLng - adjMinLng;
  const DRAW_W = SVG_W - MARGIN * 2;
  const DRAW_H = SVG_H - MARGIN * 2;

  const geoToSVG = (lat: number, lng: number) => ({
    x: MARGIN + ((lng - adjMinLng) / adjLngSpread) * DRAW_W,
    y: MARGIN + ((adjMaxLat - lat) / adjLatSpread) * DRAW_H,
  });

  type Pos = { x: number; y: number };
  const positions: Pos[] = items.map((m) => geoToSVG(m._lat, m._lng));
  const MIN_DIST = 54;
  for (let iter = 0; iter < 100; iter++) {
    let moved = false;
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const dx = positions[j].x - positions[i].x;
        const dy = positions[j].y - positions[i].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < MIN_DIST) {
          const push = dist < 0.001 ? MIN_DIST / 2 : (MIN_DIST - dist) / 2 + 0.5;
          const nx = dist < 0.001 ? Math.cos((2 * Math.PI * j) / Math.max(positions.length, 2)) : dx / dist;
          const ny = dist < 0.001 ? Math.sin((2 * Math.PI * j) / Math.max(positions.length, 2)) : dy / dist;
          positions[i].x -= nx * push; positions[i].y -= ny * push;
          positions[j].x += nx * push; positions[j].y += ny * push;
          moved = true;
        }
      }
    }
    for (const p of positions) {
      p.x = Math.max(MARGIN - 20, Math.min(SVG_W - MARGIN + 20, p.x));
      p.y = Math.max(MARGIN - 10, Math.min(SVG_H - MARGIN + 10, p.y));
    }
    if (!moved) break;
  }

  const refSVG = geoToSVG(refLat, refLng);
  const clientSVG = clientLat && clientLng && hasValidCoords(clientLat, clientLng)
    ? geoToSVG(clientLat, clientLng)
    : null;

  return (
    <div style={{
      borderRadius: 20, overflow: "hidden",
      border: "1.5px solid #e5e7eb",
      boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
      background: "#dbeafe",
    }}>
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ width: "100%", display: "block" }}>
        <defs>
          <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
            <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
          </pattern>
          <radialGradient id="bgGrad" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="#dbeafe" />
            <stop offset="100%" stopColor="#bfdbfe" />
          </radialGradient>
          <filter id="pinShadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#00000030" />
          </filter>
        </defs>

        <rect width={SVG_W} height={SVG_H} fill="url(#bgGrad)" />
        <rect width={SVG_W} height={SVG_H} fill="url(#grid)" />

        {/* Lines from source to each item */}
        {items.map((m, i) => {
          const isMagasin = m._type === 'magasin';
          const mId = isMagasin ? getMagasinId(m) : m.id;
          const pos = positions[i];
          const isSelected = isMagasin ? selectedMagasinId === mId : selectedSocieteId === mId;
          return (
            <line key={`src-line-${m._type}-${mId}`}
              x1={refSVG.x} y1={refSVG.y} x2={pos.x} y2={pos.y}
              stroke={isSelected ? "#eab308" : "rgba(100,130,255,0.18)"}
              strokeWidth={isSelected ? 2 : 1}
              strokeDasharray="6 4"
            />
          );
        })}

        {/* Lines from client to each item */}
        {clientSVG && items.map((m, i) => {
          const isMagasin = m._type === 'magasin';
          const mId = isMagasin ? getMagasinId(m) : m.id;
          const pos = positions[i];
          const isRec = isMagasin ? m._isRecommended : false;
          return (
            <line key={`cli-line-${m._type}-${mId}`}
              x1={clientSVG.x} y1={clientSVG.y} x2={pos.x} y2={pos.y}
              stroke={isRec ? "rgba(20,184,166,0.5)" : "rgba(20,184,166,0.12)"}
              strokeWidth={isRec ? 2 : 1}
              strokeDasharray="5 4"
            />
          );
        })}

        {/* Leader lines for repelled pins */}
        {items.map((m, i) => {
          const raw = geoToSVG(m._lat, m._lng);
          const pos = positions[i];
          const moved = Math.sqrt((pos.x - raw.x) ** 2 + (pos.y - raw.y) ** 2);
          const isMagasin = m._type === 'magasin';
          const mId = isMagasin ? getMagasinId(m) : m.id;
          return moved > 4 ? (
            <line key={`leader-${m._type}-${mId}`}
              x1={raw.x} y1={raw.y} x2={pos.x} y2={pos.y}
              stroke="rgba(99,102,241,0.22)" strokeWidth="1" strokeDasharray="3 3" />
          ) : null;
        })}

        {/* Source magasin (blue dot) */}
        <circle cx={refSVG.x} cy={refSVG.y} r="12" fill="rgba(59,130,246,0.18)">
          <animate attributeName="r" values="12;28;12" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0;0.5" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx={refSVG.x} cy={refSVG.y} r="10" fill="#3b82f6" filter="url(#pinShadow)" />
        <circle cx={refSVG.x} cy={refSVG.y} r="4" fill="white" />

        {/* Client pin */}
        {clientSVG && (
          <g>
            <circle cx={clientSVG.x} cy={clientSVG.y} r="10" fill="rgba(20,184,166,0.2)">
              <animate attributeName="r" values="10;22;10" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx={clientSVG.x} cy={clientSVG.y} r="9" fill="#14b8a6" filter="url(#pinShadow)" />
            <circle cx={clientSVG.x} cy={clientSVG.y} r="3.5" fill="white" />
            <rect x={clientSVG.x - 28} y={clientSVG.y + 12} width="56" height="16" rx="5"
              fill="rgba(20,184,166,0.92)" />
            <text x={clientSVG.x} y={clientSVG.y + 23} textAnchor="middle"
              fontSize="7.5" fontWeight="700" fill="white" fontFamily="system-ui">
              Client
            </text>
          </g>
        )}

        {/* Relay/Societe pins */}
        {items.map((m, i) => {
          const isMagasin = m._type === 'magasin';
          const mId = isMagasin ? getMagasinId(m) : m.id;
          const { x, y } = positions[i];
          const isSelected = isMagasin ? selectedMagasinId === mId : selectedSocieteId === mId;
          const isRec = isMagasin ? m._isRecommended : false;
          const pinColor = isSelected ? "#eab308" : isRec ? "#14b8a6" : (isMagasin ? "#6366f1" : "#f97316");
          const name = isMagasin ? m.NomMagasin : m.NomSociete;

          return (
            <g key={`pin-${m._type}-${mId}`} style={{ cursor: "pointer" }} onClick={() => isMagasin ? onSelectMagasin(mId) : onSelectSociete(mId)}>
              {isSelected && (
                <circle cx={x} cy={y} r="18" fill="rgba(234,179,8,0.22)">
                  <animate attributeName="r" values="18;36;18" dur="1.8s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.4;0;0.4" dur="1.8s" repeatCount="indefinite" />
                </circle>
              )}
              {isRec && !isSelected && (
                <circle cx={x} cy={y} r="16" fill="rgba(20,184,166,0.15)">
                  <animate attributeName="r" values="16;30;16" dur="2.2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.3;0;0.3" dur="2.2s" repeatCount="indefinite" />
                </circle>
              )}
              <circle cx={x} cy={y} r="24" fill="transparent" />
              <g transform={`translate(${x - 14}, ${y - 30})`} filter="url(#pinShadow)">
                <path d="M14 0 C6 0 0 6 0 14 C0 22 14 34 14 34 C14 34 28 22 28 14 C28 6 22 0 14 0 Z" fill={pinColor} />
                <circle cx="14" cy="14" r="6" fill="white" />
              </g>
              <rect x={x - 42} y={y + 8} width="84" height="20" rx="6"
                fill={isSelected ? "#eab308" : isRec ? "#14b8a6" : "rgba(255,255,255,0.95)"}
                stroke={isSelected ? "#ca8a04" : isRec ? "#0d9488" : "rgba(0,0,0,0.07)"}
                strokeWidth="1"
              />
              <text x={x} y={y + 18} textAnchor="middle" fontSize="8" fontWeight="700"
                fill={isSelected || isRec ? "white" : "#1a1a1a"} fontFamily="system-ui">
                {(name ?? "").slice(0, 12)}
              </text>
              <text x={x} y={y + 28} textAnchor="middle" fontSize="7" fontWeight="600"
                fill={isSelected || isRec ? "rgba(255,255,255,0.85)" : "#6b7280"} fontFamily="system-ui">
                {m._distanceToClient > 0
                  ? `${m._distanceToClient.toFixed(1)}km client`
                  : m._distanceFromSource > 0
                  ? `${m._distanceFromSource.toFixed(1)}km`
                  : "-- km"}
              </text>
            </g>
          );
        })}

        {/* Legend */}
        <rect x="12" y="12" width="152" height="78" rx="10" fill="rgba(255,255,255,0.9)" />
        <circle cx="30" cy="33" r="7" fill="#3b82f6" />
        <text x="43" y="37" fontSize="9" fill="#374151" fontWeight="600" fontFamily="system-ui">Mon magasin</text>
        <circle cx="30" cy="53" r="7" fill="#14b8a6" />
        <text x="43" y="57" fontSize="9" fill="#374151" fontWeight="600" fontFamily="system-ui">Client</text>
        
        <rect x="23" y="66" width="14" height="14" rx="3" fill="#6366f1" />
        <text x="43" y="76" fontSize="9" fill="#374151" fontWeight="600" fontFamily="system-ui">Magasin relais</text>
        
        <rect x="95" y="66" width="14" height="14" rx="3" fill="#f97316" />
        <text x="115" y="76" fontSize="9" fill="#374151" fontWeight="600" fontFamily="system-ui">Société liv.</text>
      </svg>

      <div style={{
        position: "absolute", bottom: "0.75rem", right: "0.75rem",
        padding: "0.3rem 0.65rem", background: "rgba(255,255,255,0.88)",
        borderRadius: 8, fontSize: "0.65rem", fontWeight: 700,
        color: "#6b7280", letterSpacing: "0.5px", textTransform: "uppercase",
      }}>
        Carte interactive
      </div>
    </div>
  );
};

// ─── Confirm Modal ────────────────────────────────────────────────────────────
const ConfirmModal = ({
  magasin, societe, commandeId, onConfirm, onCancel, commission, mode, submitting,
}: {
  magasin?: MagasinWithDistance | null; societe?: any; commandeId: any;
  onConfirm: () => void; onCancel: () => void; commission: number; mode: 'magasin' | 'societe';
  submitting?: boolean;
}) => (
  <div style={{
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(4px)",
    zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "1.5rem",
  }}>
    {/* ── Premium Submit Loader Overlay ── */}
    {submitting && (
      <div style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(8px)",
        zIndex: 2000,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: "1.25rem",
      }}>
        <style>{`
          @keyframes dcl-spin-ring {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes dcl-pop-in {
            0% { opacity: 0; transform: scale(0.85); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes dcl-dot-bounce {
            0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
            40% { transform: scale(1); opacity: 1; }
          }
        `}</style>
        <div style={{
          animation: "dcl-pop-in 0.3s ease both",
          display: "flex", flexDirection: "column",
          alignItems: "center", gap: "1.25rem",
        }}>
          {/* Spinner Ring */}
          <div style={{ position: "relative", width: 72, height: 72 }}>
            <div style={{
              position: "absolute", inset: 0,
              borderRadius: "50%",
              border: "4px solid rgba(255,255,255,0.1)",
            }} />
            <div style={{
              position: "absolute", inset: 0,
              borderRadius: "50%",
              border: "4px solid transparent",
              borderTopColor: "#eab308",
              animation: "dcl-spin-ring 0.75s linear infinite",
            }} />
            <div style={{
              position: "absolute", inset: 10,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #eab308, #f59e0b)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 6px 20px rgba(234,179,8,0.35)",
            }}>
              <Store size={22} color="white" strokeWidth={2} />
            </div>
          </div>
          {/* Text */}
          <div style={{ textAlign: "center" }}>
            <p style={{
              margin: 0, color: "#fff",
              fontWeight: 800, fontSize: "1.05rem", letterSpacing: "-0.2px",
            }}>
              Déplacement en cours...
            </p>
            <p style={{
              margin: "6px 0 0", color: "rgba(255,255,255,0.55)",
              fontSize: "0.82rem", fontWeight: 500,
            }}>
              Veuillez patienter
            </p>
          </div>
          {/* Dot loader */}
          <div style={{ display: "flex", gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} style={{
                width: 8, height: 8, borderRadius: "50%", background: "#eab308",
                animation: `dcl-dot-bounce 1.4s ease-in-out ${i * 0.16}s infinite`,
              }} />
            ))}
          </div>
        </div>
      </div>
    )}
    <div style={{
      background: "#fff", borderRadius: 24, padding: "2rem",
      width: "100%", maxWidth: 440,
      boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: "#eab308",
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: "1.25rem",
      }}>
        <Store size={26} color="white" strokeWidth={2} />
      </div>
      <h2 style={{ margin: "0 0 0.4rem", fontSize: "1.2rem", fontWeight: 800, color: "#111827" }}>
        Confirmer le dépôt
      </h2>
      <p style={{ margin: "0 0 1.25rem", color: "#6b7280", fontSize: "0.925rem", lineHeight: 1.6 }}>
        La commande <strong>#{commandeId}</strong> sera déposée {mode === 'magasin' ? "au magasin relais" : "à la société de livraison"} ci-dessous.
      </p>

      {mode === 'magasin' && magasin && magasin._isRecommended && (
        <div style={{
          marginBottom: "1rem", padding: "10px 14px", borderRadius: 12,
          background: "#ccfbf1", border: "1px solid #5eead4",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <Sparkles size={14} color="#0d9488" strokeWidth={2.5} />
          <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#0f766e" }}>
            Meilleure option — le plus proche de votre client
          </span>
        </div>
      )}

      <div style={{
        background: "#f9fafb", borderRadius: 14, padding: "1.1rem",
        marginBottom: "1.5rem", border: "1.5px solid #f3f4f6",
      }}>
        {mode === 'magasin' && magasin ? (
          <>
            {[
              { label: "Magasin", value: magasin.NomMagasin },
              { label: "Adresse", value: magasin.adresse_de_siege },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                <span style={{ color: "#6b7280", fontSize: "0.85rem", fontWeight: 600 }}>{label}</span>
                <span style={{ color: "#111827", fontSize: "0.9rem", fontWeight: 800, textAlign: "right", maxWidth: "58%" }}>{value}</span>
              </div>
            ))}
            {magasin._distanceToClient > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                <span style={{ color: "#6b7280", fontSize: "0.85rem", fontWeight: 600 }}>Distance du client</span>
                <span style={{ color: "#0d9488", fontSize: "0.875rem", fontWeight: 800 }}>{magasin._distanceToClient.toFixed(1)} km</span>
              </div>
            )}
            {magasin._distanceFromSource > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                <span style={{ color: "#6b7280", fontSize: "0.85rem", fontWeight: 600 }}>Distance de votre magasin</span>
                <span style={{ color: "#0369a1", fontSize: "0.875rem", fontWeight: 800 }}>{magasin._distanceFromSource.toFixed(1)} km</span>
              </div>
            )}
          </>
        ) : mode === 'societe' && societe ? (
          <>
            {[
              { label: "Société", value: societe.NomSociete },
              { label: "Adresse", value: societe.AdresseSociete },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                <span style={{ color: "#6b7280", fontSize: "0.85rem", fontWeight: 600 }}>{label}</span>
                <span style={{ color: "#111827", fontSize: "0.9rem", fontWeight: 800, textAlign: "right", maxWidth: "58%" }}>{value}</span>
              </div>
            ))}
            {societe._distanceFromSource > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                <span style={{ color: "#6b7280", fontSize: "0.85rem", fontWeight: 600 }}>Distance de votre magasin</span>
                <span style={{ color: "#0369a1", fontSize: "0.875rem", fontWeight: 800 }}>{societe._distanceFromSource.toFixed(1)} km</span>
              </div>
            )}
            {societe.Prix && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                <span style={{ color: "#6b7280", fontSize: "0.85rem", fontWeight: 600 }}>Frais de livraison</span>
                <span style={{ color: "#7e22ce", fontSize: "0.875rem", fontWeight: 800 }}>{societe.Prix === '-' ? "Non défini" : `${societe.Prix} MAD`}</span>
              </div>
            )}
          </>
        ) : null}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          paddingTop: "0.6rem", borderTop: "1px solid #f3f4f6",
        }}>
          <span style={{ color: "#6b7280", fontSize: "0.85rem", fontWeight: 600 }}>Commission</span>
          <span style={{
            background: "#eab308", color: "white",
            padding: "3px 12px", borderRadius: 8,
            fontSize: "0.875rem", fontWeight: 800,
          }}>
            {commission.toFixed(2)} MAD
          </span>
        </div>
      </div>

      <div style={{ display: "flex", gap: "0.625rem" }}>
        <button onClick={onCancel} disabled={submitting} style={{
          flex: 1, padding: "0.85rem", borderRadius: 12,
          border: "1.5px solid #e5e7eb", background: "#fff",
          color: "#374151", fontWeight: 700, fontSize: "0.9rem",
          cursor: submitting ? "not-allowed" : "pointer",
          opacity: submitting ? 0.5 : 1,
        }}>
          Annuler
        </button>
        <button onClick={onConfirm} disabled={submitting} style={{
          flex: 2, padding: "0.85rem", borderRadius: 12, border: "none",
          background: submitting ? "#374151" : "#111827", color: "white",
          fontWeight: 700, fontSize: "0.9rem",
          cursor: submitting ? "not-allowed" : "pointer",
          boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          {submitting ? (
            <>
              <div style={{
                width: 16, height: 16,
                border: "2.5px solid rgba(255,255,255,0.3)",
                borderTopColor: "#fff",
                borderRadius: "50%",
                animation: "dcl-spin-ring 0.7s linear infinite",
              }} />
              En cours...
            </>
          ) : "✓ Confirmer le dépôt"}
        </button>
      </div>
    </div>
  </div>
);


// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DeposezPage() {
  const { magasinId, commandeId: commandeIdParam } = useParams<{ magasinId: string; commandeId: any }>();
  const navigate = useNavigate();

  const [magasins, setMagasins] = useState<MagasinWithDistance[]>([]);
  const [commande, setCommande] = useState<any>(null);
  const [loadingMagasins, setLoadingMagasins] = useState(true);
  const [loadingCommande, setLoadingCommande] = useState(true);
  const [geocoding, setGeocoding] = useState(false);

  const [selectedMagasinId, setSelectedMagasinId] = useState<number | string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // ─── Société de livraison state ──────────────────────────────────────────
  
  const [rawSocietes, setRawSocietes] = useState<SocieteLivraison[]>([]);
  const [societesCoords, setSocietesCoords] = useState<Record<number, {lat: number; lng: number}>>({});
  const [selectedSocieteId, setSelectedSocieteId] = useState<number | null>(null);

  const handleSelectMagasin = (id: number | string) => { setSelectedMagasinId(id); setSelectedSocieteId(null); };
  const handleSelectSociete = (id: number | null) => { setSelectedSocieteId(id); setSelectedMagasinId(null); };


  useEffect(() => {
    axios.get(`${process.env.REACT_APP_PHP_APP_URL}/admin/societe-livraison`)
      .then((res) => {
        const active: SocieteLivraison[] = Array.isArray(res.data)
          ? res.data.filter((s: SocieteLivraison) => s.Status === 'active')
          : [];
        setRawSocietes(active);
        (async () => {
          for (const s of active) {
            if (!s.id) continue;
            const coords = await geocodeAddress(s.AdresseSociete);
            if (coords) setSocietesCoords(prev => ({ ...prev, [s.id!]: coords }));
            await new Promise(r => setTimeout(r, 900));
          }
        })();
      })
      .catch(console.error);
  }, []);

  const [refCoords, setRefCoords] = useState<{ lat: number; lng: number }>({ lat: 33.5731, lng: -7.5898 });
  const [clientCoords, setClientCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Raw normalized list (no scoring yet) — filled once magasins load
  const [rawMagasins, setRawMagasins] = useState<(MagasinInfo & { _lat: number; _lng: number })[]>([]);

  // ─── Step 1: load all magasins ───────────────────────────────────────────
  useEffect(() => {
    setLoadingMagasins(true);
    AfficheMagasinsService()
      .getMagasin()
      .then((res) => {
        const data: MagasinInfo[] = res.data ?? [];
        const currentMagasinName = localStorage.getItem("MagasinName");

        const normalized = data.map((m) => {
          // BUG FIX: read raw values then normalize (swap detection + validation)
          const rawLat = toNum((m as any).Latitude ?? (m as any).latitude ?? (m as any).lat ?? 0);
          const rawLng = toNum((m as any).Longitude ?? (m as any).longitude ?? (m as any).lng ?? 0);
          const { lat, lng } = normalizeCoords(rawLat, rawLng);
          return { ...m, _lat: lat, _lng: lng };
        });

        const myMagasin = normalized.find((m) => m.NomMagasin === currentMagasinName);

        // Only use my magasin's coords if they are valid
        const refLat = myMagasin && hasValidCoords(myMagasin._lat, myMagasin._lng)
          ? myMagasin._lat : 33.5731;
        const refLng = myMagasin && hasValidCoords(myMagasin._lat, myMagasin._lng)
          ? myMagasin._lng : -7.5898;
        setRefCoords({ lat: refLat, lng: refLng });

        // BUG FIX: exclude my own magasin AND any magasin with invalid/zero coords
        const others = normalized.filter(
          (m) => m.NomMagasin !== currentMagasinName && hasValidCoords(m._lat, m._lng)
        );
        setRawMagasins(others);

        // Debug log — remove in production
        console.group("DeposezPage — magasin coords");
        normalized.forEach((m) =>
          console.log(
            `${m.NomMagasin}: lat=${m._lat}, lng=${m._lng}, valid=${hasValidCoords(m._lat, m._lng)}`
          )
        );
        console.groupEnd();
      })
      .catch(() => setRawMagasins([]))
      .finally(() => setLoadingMagasins(false));
  }, []);

  // ─── Step 2: load commande ───────────────────────────────────────────────
  useEffect(() => {
    if (!commandeIdParam) { setLoadingCommande(false); return; }
    setLoadingCommande(true);
    HistoriqueCommande()
      .getCommande()
      .then((res) => {
        const all: any[] = res.data ?? [];
        setCommande(
          all.find((cmd) => String(cmd.IdCommande) === String(commandeIdParam)) ?? null
        );
      })
      .catch(() => setCommande(null))
      .finally(() => setLoadingCommande(false));
  }, [commandeIdParam]);

  // ─── Step 2.5: Auto-select societe if commande has SocieteLivraison or MagasinRetrait ──
  useEffect(() => {
    if (!rawSocietes.length || !commande) return;
    if (selectedSocieteId !== null || selectedMagasinId !== null) return;
    const targetName = (commande.SocieteLivraison || commande.MagasinRetrait || "").toLowerCase().trim();
    if (targetName) {
      const match = rawSocietes.find(
        (s) => s.NomSociete.toLowerCase().trim() === targetName || (targetName.includes("amana") && s.NomSociete.toLowerCase().includes("amana"))
      );
      if (match && match.id) {
        setSelectedSocieteId(match.id);
      }
    }
  }, [rawSocietes, commande, selectedSocieteId, selectedMagasinId]);

  // ─── Step 3: geocode client address once commande is known ───────────────
  useEffect(() => {
    if (!commande?.Adresse) return;
    setGeocoding(true);
    geocodeAddress(commande.Adresse)
      .then((coords) => {
        setClientCoords(coords);
        // Debug log — remove in production
        console.log("Client geocoded coords:", coords);
      })
      .finally(() => setGeocoding(false));
  }, [commande?.Adresse]);

  // ─── Step 4: compute distances & score ──────────────────────────────────
  // rawMagasins already has invalid coords filtered out (Step 1), so every
  // magasin here has a real geographic position. haversineKm will return
  // accurate results without null-island interference.
  useEffect(() => {
    if (rawMagasins.length === 0) return;

    const withDistances: MagasinWithDistance[] = rawMagasins.map((m) => {
      const distFromSource = haversineKm(refCoords.lat, refCoords.lng, m._lat, m._lng);
      const distToClient = clientCoords
        ? haversineKm(m._lat, m._lng, clientCoords.lat, clientCoords.lng)
        : 0;
      const score = clientCoords
        ? scoreRelay(m._lat, m._lng, clientCoords.lat, clientCoords.lng, refCoords.lat, refCoords.lng)
        : distFromSource;
      return {
        ...m,
        _distanceFromSource: distFromSource,
        _distanceToClient: distToClient,
        _score: score,
        _isRecommended: false,
      };
    });

    if (withDistances.length > 0) {
      const bestIdx = withDistances.reduce(
        (best, m, i) => (m._score < withDistances[best]._score ? i : best),
        0
      );
      withDistances[bestIdx]._isRecommended = true;
    }

    withDistances.sort((a, b) => {
      if (a._isRecommended && !b._isRecommended) return -1;
      if (!a._isRecommended && b._isRecommended) return 1;
      return a._score - b._score;
    });

    setMagasins(withDistances);

    setSelectedMagasinId((prev) => {
      if (prev !== null) return prev;
      const rec = withDistances.find((m) => m._isRecommended);
      return rec ? getMagasinId(rec) : null;
    });
  }, [rawMagasins, refCoords, clientCoords]);

  const societesWithDistances = rawSocietes.map(s => {
    const coords = s.id ? societesCoords[s.id] : null;
    const dist = coords ? haversineKm(refCoords.lat, refCoords.lng, coords.lat, coords.lng) : -1;
    return { ...s, _distanceFromSource: dist, _lat: coords?.lat, _lng: coords?.lng };
  });
  const selectedSociete = societesWithDistances.find(s => s.id === selectedSocieteId) ?? null;

  const isLoading = loadingMagasins || loadingCommande;
  const selectedMagasin = magasins.find((m) => getMagasinId(m) === selectedMagasinId) ?? null;
  const totalHT: number = commande?.TotalCommandeHT ?? 0;
  const commission = totalHT * 0.02;
  const commandeId = commande?.IdCommande ? String(commande.IdCommande) : (commandeIdParam ?? "");
  const clientCoordsAvailable = clientCoords !== null;

  const [successToast, setSuccessToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    const valid = (selectedMagasinId && selectedMagasin && commande) || (selectedSocieteId && selectedSociete && commande);
    if (!valid) return;
    setSubmitting(true);
    try {
      const currentMagasinName = localStorage.getItem("MagasinName") || "";
      const intermediaire = selectedMagasinId 
        ? (selectedMagasin!.NomMagasin || "")
        : `[Société] ${selectedSociete!.NomSociete}`;
      await DeplacementService().addDeplacement({
        MagasinName: currentMagasinName,
        MagasinIntermediaire: intermediaire,
        IdCommande: commandeId,
        Client: commande!.NomClient || commande!.Client || "",
        Adresse: commande!.Adresse || "",
        Commision: commission,
      });
      setShowConfirm(false);
      setSuccessToast(true);
      // Redirect to deplacement list after 2 seconds
      setTimeout(() => {
        navigate(`/magasins/${magasinId}/deplacement`);
      }, 2000);
    } catch (error) {
      console.error("Erreur lors de la création du déplacement:", error);
      alert("Erreur lors de la création du déplacement. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Loading — Premium Skeleton Loader ─────────────────────────────────
  if (isLoading) return (
    <>
      <NewSideBarMagasins />
      <style>{`
        @keyframes dcl-shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        @keyframes dcl-pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 0; }
          100% { transform: scale(0.8); opacity: 0.5; }
        }
        @keyframes dcl-fade-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes dcl-icon-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        .dcl-skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
          background-size: 800px 100%;
          animation: dcl-shimmer 1.6s ease-in-out infinite;
          border-radius: 10px;
        }
        .dcl-skeleton-dark {
          background: linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 37%, #e8e8e8 63%);
          background-size: 800px 100%;
          animation: dcl-shimmer 1.6s ease-in-out infinite;
          border-radius: 10px;
        }
      `}</style>
      <div style={{
        minHeight: "100vh",
        background: "#f9fafb",
      }}>
        <div style={{
          maxWidth: 820,
          margin: "0 auto",
          padding: "1.5rem 1rem 4rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.25rem",
        }}>

          {/* ── Loader Header with Brand Icon ── */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem 0 0.5rem",
            animation: "dcl-fade-in 0.4s ease both",
          }}>
            <div style={{ position: "relative", marginBottom: "1rem" }}>
              <div style={{
                position: "absolute",
                inset: -8,
                borderRadius: "50%",
                border: "2.5px solid rgba(234,179,8,0.25)",
                animation: "dcl-pulse-ring 2s ease-in-out infinite",
              }} />
              <div style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #eab308, #f59e0b)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(234,179,8,0.25)",
                animation: "dcl-icon-pulse 2s ease-in-out infinite",
              }}>
                <Package size={26} color="white" strokeWidth={2} />
              </div>
            </div>
            <p style={{
              margin: 0,
              fontWeight: 800,
              fontSize: "1rem",
              color: "#111827",
              letterSpacing: "-0.2px",
            }}>
              Chargement du déplacement
            </p>
            <p style={{
              margin: "4px 0 0",
              fontWeight: 500,
              fontSize: "0.82rem",
              color: "#9ca3af",
            }}>
              Préparation des magasins relais…
            </p>
          </div>

          {/* ── Skeleton: Address Alert Card ── */}
          <div style={{
            background: "#fff",
            borderRadius: 16,
            padding: "1.1rem 1.25rem",
            border: "1.5px solid #f3f4f6",
            display: "flex",
            gap: "0.875rem",
            alignItems: "flex-start",
            animation: "dcl-fade-in 0.4s ease 0.1s both",
          }}>
            <div className="dcl-skeleton" style={{ width: 40, height: 40, borderRadius: 11, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div className="dcl-skeleton" style={{ width: "55%", height: 14, marginBottom: 8 }} />
              <div className="dcl-skeleton" style={{ width: "80%", height: 11 }} />
            </div>
          </div>

          {/* ── Skeleton: Map Area ── */}
          <div style={{ animation: "dcl-fade-in 0.4s ease 0.18s both" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "0.75rem" }}>
              <div className="dcl-skeleton" style={{ width: 16, height: 16, borderRadius: "50%" }} />
              <div className="dcl-skeleton" style={{ width: 160, height: 14 }} />
            </div>
            <div className="dcl-skeleton-dark" style={{
              width: "100%",
              height: 220,
              borderRadius: 20,
              position: "relative",
              overflow: "hidden",
            }}>
              {/* Fake map dots */}
              <div style={{ position: "absolute", top: "30%", left: "25%" }}>
                <div className="dcl-skeleton" style={{ width: 18, height: 18, borderRadius: "50%" }} />
              </div>
              <div style={{ position: "absolute", top: "50%", left: "55%" }}>
                <div className="dcl-skeleton" style={{ width: 14, height: 14, borderRadius: "50%" }} />
              </div>
              <div style={{ position: "absolute", top: "35%", left: "72%" }}>
                <div className="dcl-skeleton" style={{ width: 16, height: 16, borderRadius: "50%" }} />
              </div>
              <div style={{ position: "absolute", top: "65%", left: "40%" }}>
                <div className="dcl-skeleton" style={{ width: 12, height: 12, borderRadius: "50%" }} />
              </div>
            </div>
          </div>

          {/* ── Skeleton: List Header ── */}
          <div style={{ animation: "dcl-fade-in 0.4s ease 0.25s both" }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "0.75rem",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div className="dcl-skeleton" style={{ width: 16, height: 16, borderRadius: "50%" }} />
                <div className="dcl-skeleton" style={{ width: 180, height: 14 }} />
              </div>
              <div className="dcl-skeleton" style={{ width: 90, height: 22, borderRadius: 8 }} />
            </div>

            {/* ── Skeleton: Magasin Cards (4 cards) ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} style={{
                  background: "#fff",
                  borderRadius: 16,
                  padding: "1.1rem 1.25rem",
                  border: "1.5px solid #f3f4f6",
                  display: "flex",
                  gap: "0.875rem",
                  alignItems: "flex-start",
                  animation: `dcl-fade-in 0.4s ease ${0.3 + i * 0.08}s both`,
                }}>
                  <div className="dcl-skeleton" style={{
                    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  }} />
                  <div style={{ flex: 1 }}>
                    <div className="dcl-skeleton" style={{ width: `${60 - i * 5}%`, height: 14, marginBottom: 8 }} />
                    <div className="dcl-skeleton" style={{ width: "75%", height: 10, marginBottom: 10 }} />
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                      <div className="dcl-skeleton" style={{ width: 80, height: 22, borderRadius: 8 }} />
                      <div className="dcl-skeleton" style={{ width: 70, height: 22, borderRadius: 8 }} />
                      <div className="dcl-skeleton" style={{ width: 60, height: 22, borderRadius: 8 }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Skeleton: CTA Button ── */}
          <div style={{ animation: "dcl-fade-in 0.4s ease 0.55s both" }}>
            <div className="dcl-skeleton-dark" style={{
              width: "100%",
              height: 52,
              borderRadius: 14,
            }} />
          </div>

        </div>
      </div>
    </>
  );


  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; background: #f9fafb; font-family: system-ui, -apple-system, sans-serif; }
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .deposez-scroll::-webkit-scrollbar { width: 5px; }
        .deposez-scroll::-webkit-scrollbar-track { background: transparent; }
        .deposez-scroll::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      <NewSideBarMagasins />

      <div style={{ minHeight: "100vh", background: "#f9fafb" }}>
        <div style={{
          maxWidth: 820, margin: "0 auto",
          padding: "1.5rem 1rem 4rem",
          display: "flex", flexDirection: "column", gap: "1.25rem",
        }}>

          {/* ── Address alert ── */}
          {commande?.Adresse && (
            <div style={{
              background: "#fff", borderRadius: 16, padding: "1.1rem 1.25rem",
              border: "1.5px solid #fee2e2",
              display: "flex", gap: "0.875rem", alignItems: "flex-start",
              animation: "slideUp 0.35s ease",
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 11, background: "#fee2e2",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <AlertCircle size={20} color="#dc2626" strokeWidth={2} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 3px", fontWeight: 800, fontSize: "0.9rem", color: "#111827" }}>
                  Adresse différente détectée
                </p>
                <p style={{ margin: 0, fontSize: "0.8rem", color: "#6b7280", lineHeight: 1.5 }}>
                  Client : <strong>{commande.Adresse}</strong>
                </p>
              </div>
              {geocoding && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 5,
                  color: "#9ca3af", fontSize: "0.78rem", fontWeight: 600, flexShrink: 0,
                }}>
                  <div style={{
                    width: 13, height: 13, border: "2px solid #e5e7eb",
                    borderTopColor: "#14b8a6", borderRadius: "50%",
                    animation: "spin 0.7s linear infinite",
                  }} />
                  Géolocalisation...
                </div>
              )}
              {clientCoordsAvailable && !geocoding && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 4,
                  color: "#0d9488", fontSize: "0.78rem", fontWeight: 700, flexShrink: 0,
                }}>
                  <CheckCircle size={13} strokeWidth={2.5} />
                  Localisé
                </div>
              )}
            </div>
          )}

          {/* ── Recommendation banner ── */}
          {clientCoordsAvailable && magasins.some((m) => m._isRecommended) && (
            <div style={{
              background: "linear-gradient(135deg,#ccfbf1,#99f6e4)",
              borderRadius: 14, padding: "0.875rem 1.1rem",
              border: "1.5px solid #5eead4",
              display: "flex", alignItems: "center", gap: 10,
              animation: "slideUp 0.35s ease",
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10, background: "#0d9488",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <Sparkles size={16} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <p style={{ margin: "0 0 2px", fontWeight: 800, fontSize: "0.875rem", color: "#0f766e" }}>
                  Recommandation activée
                </p>
                <p style={{ margin: 0, fontSize: "0.78rem", color: "#0d9488", fontWeight: 500 }}>
                  Le magasin le plus proche du client a été pré-sélectionné et mis en tête de liste.
                </p>
              </div>
            </div>
          )}

          {/* ── Map ── */}
          {(magasins.length > 0 || societesWithDistances.length > 0) && (
            <div style={{ animation: "slideUp 0.35s ease 0.05s both", position: "relative", marginBottom: "1rem" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                marginBottom: "0.75rem",
              }}>
                <MapPin size={16} color="#eab308" strokeWidth={2.5} />
                <h2 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 800, color: "#111827" }}>
                  Carte des points de relais et livraison
                </h2>
              </div>
              <InteractiveMap
                magasins={magasins}
                societes={societesWithDistances}
                selectedMagasinId={selectedMagasinId}
                selectedSocieteId={selectedSocieteId}
                onSelectMagasin={handleSelectMagasin}
                onSelectSociete={handleSelectSociete}
                refLat={refCoords.lat}
                refLng={refCoords.lng}
                clientLat={clientCoords?.lat ?? null}
                clientLng={clientCoords?.lng ?? null}
              />
            </div>
          )}

          {/* ── List ── */}
          <div style={{ animation: "slideUp 0.35s ease 0.1s both", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "0.8rem" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#111827", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  Options de livraison
                </h2>
                <p style={{ margin: "2px 0 0", fontSize: "0.8rem", color: "#6b7280", fontWeight: 500 }}>
                  Sélectionnez un point de relais ou une société de livraison
                </p>
              </div>
              <div style={{ background: "#f3f4f6", padding: "0.25rem 0.6rem", borderRadius: 8 }}>
                <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#4b5563" }}>
                  {magasins.length + societesWithDistances.length} disponibles
                </span>
              </div>
            </div>

            <div className="deposez-scroll" style={{ display: "flex", flexDirection: "column", gap: "0.625rem", maxHeight: 520, overflowY: "auto", paddingRight: "0.2rem" }}>
              {/* Magasins */}
              {magasins.map((m) => {
                const mId = getMagasinId(m);
                return (
                  <MagasinCard
                    key={`magasin-${mId}`}
                    magasin={m}
                    selected={selectedMagasinId === mId}
                    onClick={() => handleSelectMagasin(mId)}
                    commissionAmount={commission}
                    clientCoordsAvailable={clientCoordsAvailable}
                  />
                );
              })}

              {/* Societes */}
              {societesWithDistances.map((s) => (
                <SocieteCard
                  key={`societe-${s.id}`}
                  societe={s}
                  selected={selectedSocieteId === s.id}
                  onClick={() => handleSelectSociete(s.id ?? null)}
                />
              ))}

              {magasins.length === 0 && societesWithDistances.length === 0 && (
                <div style={{ textAlign: "center", padding: "2.5rem", color: "#9ca3af", background: "#fff", borderRadius: 16, border: "1.5px dashed #e5e7eb" }}>
                  <Store size={28} color="#d1d5db" />
                  <p style={{ marginTop: "0.6rem", fontWeight: 600 }}>Aucune option de livraison disponible</p>
                </div>
              )}
            </div>
          </div>

          {/* ── CTA ── */}
          <div style={{ animation: "slideUp 0.35s ease 0.15s both", marginTop: "1rem" }}>
            {(() => {
              const hasSelection = !!selectedMagasinId || !!selectedSocieteId;
              return (
                <button
                  disabled={!hasSelection}
                  onClick={() => setShowConfirm(true)}
                  style={{
                    width: "100%", padding: "1.05rem", borderRadius: 14, border: "none",
                    background: hasSelection ? "#111827" : "#e5e7eb",
                    color: hasSelection ? "#fff" : "#9ca3af",
                    fontWeight: 800, fontSize: "0.95rem",
                    cursor: hasSelection ? "pointer" : "not-allowed",
                    transition: "all 0.2s ease",
                    boxShadow: hasSelection ? "0 4px 18px rgba(0,0,0,0.18)" : "none",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "0.6rem",
                  }}
                  onMouseEnter={(e) => { if (hasSelection) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.24)"; } }}
                  onMouseLeave={(e) => { if (hasSelection) { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(0,0,0,0.18)"; } }}
                >
                  {selectedMagasinId ? (
                    <><CheckCircle size={18} strokeWidth={2.5} />Confirmer le dépôt chez {selectedMagasin?.NomMagasin}{selectedMagasin?._isRecommended && <span style={{ fontSize: "0.7rem", background: "rgba(255,255,255,0.15)", padding: "2px 8px", borderRadius: 6, fontWeight: 700 }}>✦ Recommandé</span>}<ChevronRight size={18} strokeWidth={2.5} /></>
                  ) : selectedSocieteId ? (
                    <><CheckCircle size={18} strokeWidth={2.5} />Confirmer avec {selectedSociete?.NomSociete}<ChevronRight size={18} strokeWidth={2.5} /></>
                  ) : (
                    <><Store size={18} strokeWidth={2} />Sélectionnez une option de livraison pour continuer</>
                  )}
                </button>
              );
            })()}
          </div>
        </div>
      </div>

      {showConfirm && (
        <ConfirmModal
          magasin={selectedMagasin ?? undefined}
          societe={selectedSociete ?? undefined}
          commandeId={commandeId}
          commission={commission}
          mode={selectedSocieteId ? 'societe' : 'magasin'}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
          submitting={submitting}
        />
      )}

      {/* Success Toast Notification */}
      {successToast && (
        <div style={{
          position: "fixed",
          bottom: "2rem",
          left: "50%",
          transform: "translateX(-50%)",
          background: "#111827",
          color: "white",
          padding: "1rem 1.5rem",
          borderRadius: 16,
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          zIndex: 9999,
          animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: "#10b981", display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <CheckCircle size={16} color="white" strokeWidth={3} />
          </div>
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: "0.95rem" }}>Déplacement réussi !</p>
            <p style={{ margin: "2px 0 0", color: "#9ca3af", fontSize: "0.8rem" }}>Redirection en cours...</p>
          </div>
        </div>
      )}
    </>
  );
}